import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../PayRent/PayRentPage.css';

const STAR_LABELS = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

// Standalone, public page — a tenant opens this from a link the owner shares
// (WhatsApp/SMS), no account or login required. Rendered outside the normal
// app shell (see App.js), so it has no Navbar/context dependency.
export default function RateStayPage({ tenantId }) {
  const [loading, setLoading]         = useState(true);
  const [info, setInfo]               = useState(null);
  const [error, setError]             = useState('');
  const [rating, setRating]           = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment]         = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [done, setDone]               = useState(false);

  useEffect(() => {
    axios.get(`/api/tenants/public/${tenantId}`)
      .then(res => { if (res.data.success) setInfo(res.data.data); else setError('This link is invalid or has expired.'); })
      .catch(() => setError('This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const submit = async () => {
    if (!rating) { setError('Please select a star rating.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(`/api/tenants/public/${tenantId}/review`, { rating, comment });
      if (!data.success) throw new Error(data.message);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not submit your review.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pay-rent-page">
        <div className="pay-rent-card">
          <div className="spinner" style={{ width: 32, height: 32, borderColor: '#eee', borderTopColor: '#E53935', margin: '40px auto' }} />
        </div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="pay-rent-page">
        <div className="pay-rent-card">
          <div className="pay-rent-icon">⚠️</div>
          <h2>Link Not Valid</h2>
          <p className="pay-rent-error">{error}</p>
        </div>
      </div>
    );
  }

  if (done || info.existingReview) {
    return (
      <div className="pay-rent-page">
        <div className="pay-rent-card">
          <div className="pay-rent-icon">✅</div>
          <h2>Thanks for your feedback!</h2>
          <p>Your rating for <strong>{info.ownerName}</strong> has been recorded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pay-rent-page">
      <div className="pay-rent-card">
        <div className="pay-rent-logo">📍 Spaczo</div>
        <h2>Rate Your Stay</h2>
        <div className="pay-rent-details">
          <div className="pay-rent-row"><span>Property</span><strong>{info.propertyTitle}</strong></div>
          <div className="pay-rent-row"><span>Owner</span><strong>{info.ownerName}</strong></div>
        </div>
        <p style={{ fontSize: 13, color: '#64748B', marginBottom: 4 }}>
          How was your experience renting from {info.ownerName}?
        </p>
        <div className="star-picker">
          {[1, 2, 3, 4, 5].map(n => (
            <span
              key={n}
              className={`star ${(hoverRating || rating) >= n ? 'filled' : ''}`}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHoverRating(n)}
              onMouseLeave={() => setHoverRating(0)}
            >★</span>
          ))}
        </div>
        {(hoverRating || rating) > 0 && <div className="star-label">{STAR_LABELS[(hoverRating || rating) - 1]}</div>}
        <textarea
          className="pay-rent-textarea"
          placeholder="Anything else you'd like to share? (optional)"
          value={comment}
          onChange={e => setComment(e.target.value)}
          maxLength={1000}
        />
        {error && <div className="pay-rent-error">{error}</div>}
        <button className="pay-rent-btn" onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Rating'}
        </button>
        <p className="pay-rent-note">
          Your rating helps future tenants and contributes to {info.ownerName}'s public landlord rating on Spaczo.
        </p>
      </div>
    </div>
  );
}
